// @ts-nocheck

import { colors } from '@lmc/common/constants'
import { Text } from '@lmc/ui-kit'
import { includes } from 'lodash'
import { Suspense, useEffect } from 'react'
import Field from '../../../fields'
import { HelpLink } from '../../../generic'

interface EmarStockAlertFieldProps {
  editState: boolean
  fields: Record<string, any>
  formId: string
  setFormField: (key: string, value: any) => void
  testId?: string
}

const ALERT_LEVEL_ONE_UNITS: string[] = [
  'bottle',
  'box',
  'inhaler',
  'tube',
  'tub'
]
const ALERT_LEVEL_ONE_HUNDRED_UNITS: string[] = ['ml', 'gram', 'mg']

const getDefaultAlertLevel = (stockQuantityType: string): number => {
  return includes(ALERT_LEVEL_ONE_UNITS, stockQuantityType)
    ? 1
    : includes(ALERT_LEVEL_ONE_HUNDRED_UNITS, stockQuantityType)
      ? 100
      : 10
}

/**
 * Returns stock alert field
 * @param {EmarStockAlertFieldProps} props - The component's props.
 * @returns {JSX.Element} EmarStockAlertField.
 */
export default function EmarStockAlertField(
  props: EmarStockAlertFieldProps
): JSX.Element {
  const { editState, fields, formId, setFormField, testId } = props
  const { stockQuantityType } = fields

  useEffect(() => {
    if (!!stockQuantityType && !editState) {
      const alertLevel = getDefaultAlertLevel(stockQuantityType)
      setFormField({
        key: 'stockAlertLevel',
        value: alertLevel
      })
    }
  }, [stockQuantityType, setFormField, editState])

  const Label = () => (
    <div
      style={styles.labelContainer}
      data-testid={testId ? `${testId}-stock-alert-label` : 'stock-alert-label'}
    >
      <div>
        <Text as="h5" style={styles.label}>
          Stock Alert Level
        </Text>
        <div style={styles.sublabel}>
          If the stock level drops below this value, an alert will be displayed.
        </div>
      </div>
      <HelpLink supportLink="http://support.logmycare.co.uk/en/articles/5390993-how-do-i-set-a-stock-alert-level" />
    </div>
  )

  return (
    <div
      style={styles.container}
      data-testid={testId ? `${testId}-stock-alert-field` : 'stock-alert-field'}
    >
      <Label />
      <div style={styles.fieldContainer}>
        <div style={styles.field}>
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              formFieldStyle={{ maxWidth: 120 }}
              formId={formId}
              keys="stockAlertLevel"
              layout="no-label"
              positiveValuesOnly
              type="number"
              validationTypes="nonZero"
              values={fields}
            />
          </Suspense>
        </div>
        <div style={styles.stockUnitText}>{stockQuantityType}</div>
      </div>
    </div>
  )
}

const styles = {
  container: {
    background: 'white',
    borderRadius: 15,
    padding: 16,
    marginTop: 16
  },
  field: {
    maxWidth: 120
  },
  fieldContainer: {
    alignItems: 'center',
    display: 'flex'
  },
  label: {},
  labelContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 16
  },
  stockUnitText: {
    marginLeft: 12
  },
  sublabel: {
    color: colors.darkgrey,
    fontSize: 12,
    lineHeight: '18px'
  }
}
